import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ToastAction } from '@/components/ui/toast'
import api from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import { useAffiliatePreview } from '@/hooks/useAffiliatePreview'
import { PageHeader } from '@/components/dashboard-ui'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ArrowRight, Copy, Eye, ExternalLink, Mail, PhoneOutgoing, RotateCw, Users, WalletCards, BookOpen } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface AffiliateMe {
  affiliate: {
    id: string
    status: string
    displayName: string
    defaultCommissionPct: number
    commissionTermMonths: number
    commissionUnlimited: boolean
    sandboxOrganization: { id: string; name: string; slug: string } | null
    demoPhoneNumber: string | null
  }
  links: Array<{ id: string; code: string; label: string; active: boolean; url: string }>
  summary: {
    referrals: number
    converted: number
    pending_cents: number
    approved_cents: number
    paid_cents: number
  }
}

function formatPhoneDisplay(e164: string | null): string {
  if (!e164) return ''
  const digits = e164.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 11)}`
  }
  return e164
}

const money = (cents: number | string | null | undefined) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(Number(cents || 0) / 100)

const date = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '-'

export default function AffiliateDashboardPage() {
  const { toast } = useToast()
  const navigate = useNavigate()
  const { isPreview, apiParams, linkQuery } = useAffiliatePreview()
  const [me, setMe] = useState<AffiliateMe | null>(null)
  const [referrals, setReferrals] = useState<any[]>([])
  const [commissions, setCommissions] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [resetOpen, setResetOpen] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [outboundOpen, setOutboundOpen] = useState(false)
  const [outboundForm, setOutboundForm] = useState({ phoneNumber: '', firstName: '', caseType: '' })
  const [outboundCalling, setOutboundCalling] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [meRes, referralsRes, commissionsRes] = await Promise.all([
        api.get('/api/affiliates/me', { params: apiParams }),
        api.get('/api/affiliates/me/referrals', { params: apiParams }),
        api.get('/api/affiliates/me/commissions', { params: apiParams }),
      ])
      setMe(meRes.data)
      setReferrals(referralsRes.data.referrals || [])
      setCommissions(commissionsRes.data.commissions || [])
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setMe(null)
      } else {
        toast({
          title: 'Failed to load affiliate dashboard',
          description: err?.response?.data?.message || err?.message || 'Please try again.',
          variant: 'destructive',
        })
      }
    } finally {
      setLoading(false)
    }
  }, [toast, apiParams])

  useEffect(() => { load() }, [load])

  // Deep-link support: /affiliate?reset=1 auto-opens the reset dialog. The
  // demo banner's "Reset demo" link points here — landing on the dashboard
  // with the dialog already open is one less step than navigating, then
  // hunting for the reset button.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    if (searchParams.get('reset') === '1') {
      setResetOpen(true)
      // Strip the param so a refresh doesn't re-open the dialog.
      const next = new URLSearchParams(searchParams)
      next.delete('reset')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams])

  async function copyLink(url: string) {
    try {
      await navigator.clipboard.writeText(url)
      toast({ title: 'Affiliate link copied' })
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' })
    }
  }

  // Open the public-facing preview page (the law-firm landing site with the
  // chat widget embedded) in a new tab. This is what the prospect sees during
  // a demo. Sellers use it to walk through the experience live, AND to verify
  // their wizard config landed on the right firm name / practice areas before
  // sharing the link with a prospect.
  function openPreview() {
    if (!me?.affiliate.sandboxOrganization) return
    const url = `${window.location.origin}/preview/${encodeURIComponent(me.affiliate.sandboxOrganization.slug)}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  async function resetDemo() {
    setResetting(true)
    try {
      const res = await api.post('/api/affiliates/me/reset-demo')
      const w = res.data.wiped || {}
      const agentStatus = res.data.assistantWired
        ? ' Default demo agent is ready.'
        : ' Default demo agent could not be confirmed; run the wizard before live voice demos.'
      toast({
        title: 'Demo reset',
        description: `Wiped ${w.voiceAssistants || 0} agent(s), ${w.voiceCalls || 0} call(s), ${w.conversations || 0} chat(s). Phone & sample leads kept.${agentStatus}`,
      })
      setResetOpen(false)
      await load()
    } catch (err: any) {
      toast({
        title: 'Reset failed',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    } finally {
      setResetting(false)
    }
  }

  async function fireDemoOutbound() {
    setOutboundCalling(true)
    try {
      const res = await api.post('/api/affiliates/me/demo-outbound', outboundForm)
      toast({
        title: 'Demo call initiated',
        description: `Molly is calling ${outboundForm.phoneNumber} now. Pick up to hear the demo.`,
      })
      setOutboundOpen(false)
      setOutboundForm({ phoneNumber: '', firstName: '', caseType: '' })
      void res
    } catch (err: any) {
      const description = err?.response?.data?.message || err?.message
      // Specific friendly handling for "no agent yet" — close the dialog
      // and add a one-tap action that takes the seller straight to the
      // wizard. Otherwise the seller has to mentally translate "go to AI
      // Agents" while staring at a sticky dialog.
      const isNoAgent = typeof description === 'string' && /no ai agent/i.test(description)
      if (isNoAgent) {
        setOutboundOpen(false)
        toast({
          title: 'Run the AI Agent Wizard first',
          description,
          variant: 'destructive',
          action: (
            <ToastAction altText="Open AI Agents" onClick={() => navigate('/agents')}>
              Open AI Agents
            </ToastAction>
          ),
        })
      } else {
        toast({
          title: 'Call failed',
          description,
          variant: 'destructive',
        })
      }
    } finally {
      setOutboundCalling(false)
    }
  }

  function copyWidgetPreviewUrl() {
    if (!me?.affiliate.sandboxOrganization) return
    const url = `${window.location.origin}/preview/${encodeURIComponent(me.affiliate.sandboxOrganization.slug)}`
    void copyLink(url)
  }

  if (loading) {
    return <div className="py-12 text-center text-sm text-muted-foreground">Loading affiliate dashboard...</div>
  }

  if (!me) {
    return (
      <div className="space-y-6">
        <PageHeader title="Affiliate Dashboard" subtitle="Affiliate access is available after LegalLady approves your application." />
        <Card className="card-inset">
          <CardContent className="py-10 text-center">
            <p className="text-sm text-muted-foreground">This account is not approved as an affiliate yet.</p>
            <Button asChild className="mt-4">
              <Link to="/affiliate/apply">Apply to become an affiliate</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const primaryLink = me.links.find(link => link.active) || me.links[0]

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={isPreview ? `Affiliate · Preview of ${me.affiliate.displayName}` : 'Affiliate'}
        title="Affiliate Dashboard"
        subtitle={
          isPreview
            ? 'Read-only preview of this affiliate\'s dashboard. Demo reset, demo outbound, and email sends are disabled.'
            : 'Track referred clients, commission status, and your demo workspace.'
        }
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to={`/affiliate/walkthrough${linkQuery}`}>
              <BookOpen className="mr-2 h-4 w-4" />
              Walkthrough &amp; FAQ
            </Link>
          </Button>
        }
      />

      {isPreview && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
          <Eye className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="flex-1">
            <div className="font-medium">Preview mode</div>
            <div className="mt-0.5 text-xs">
              You're viewing this affiliate's dashboard as a platform admin. All write actions are disabled.
            </div>
          </div>
          <Button asChild size="sm" variant="outline" className="shrink-0">
            <Link to="/admin/affiliates">Exit preview</Link>
          </Button>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="card-inset">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <Users className="h-4 w-4" /> Referrals
            </div>
            <div className="mt-1 text-2xl font-semibold">{me.summary.referrals || 0}</div>
          </CardContent>
        </Card>
        <Card className="card-inset">
          <CardContent className="py-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Converted</div>
            <div className="mt-1 text-2xl font-semibold">{me.summary.converted || 0}</div>
          </CardContent>
        </Card>
        <Card className="card-inset">
          <CardContent className="py-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Pending</div>
            <div className="mt-1 text-2xl font-semibold">{money(me.summary.pending_cents)}</div>
          </CardContent>
        </Card>
        <Card className="card-inset">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <WalletCards className="h-4 w-4" /> Paid
            </div>
            <div className="mt-1 text-2xl font-semibold">{money(me.summary.paid_cents)}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="card-inset">
        <CardHeader>
          <CardTitle className="text-base">Affiliate link</CardTitle>
          <CardDescription>
            {me.affiliate.defaultCommissionPct}% commission · {me.affiliate.commissionUnlimited ? 'Unlimited term' : `${me.affiliate.commissionTermMonths} month term`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {primaryLink ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1 rounded-md border bg-muted/40 px-3 py-2 font-mono text-xs">
                <span className="break-all">{primaryLink.url}</span>
              </div>
              <Button onClick={() => copyLink(primaryLink.url)} size="sm">
                <Copy className="mr-2 h-4 w-4" /> Copy
              </Button>
              <Button asChild variant="outline" size="sm">
                <a href={primaryLink.url} target="_blank" rel="noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" /> Open
                </a>
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No active affiliate link yet.</p>
          )}
        </CardContent>
      </Card>

      <Card className="card-inset">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <CardTitle className="text-base">Demo workspace</CardTitle>
              <CardDescription>
                Run live demos with prospects. Reset between calls; the phone number stays.
              </CardDescription>
            </div>
            <Button onClick={openPreview} size="sm" disabled={!me.affiliate.sandboxOrganization}>
              <ExternalLink className="mr-2 h-4 w-4" /> Open preview
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border bg-muted/30 px-3 py-2.5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Demo phone number</div>
              <div className="mt-0.5 font-mono text-sm font-semibold">
                {me.affiliate.demoPhoneNumber
                  ? formatPhoneDisplay(me.affiliate.demoPhoneNumber)
                  : <span className="text-muted-foreground font-normal">Not provisioned yet</span>}
              </div>
              {me.affiliate.demoPhoneNumber && (
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  Hand to prospects to call during demos.
                </div>
              )}
            </div>
            <div className="rounded-md border bg-muted/30 px-3 py-2.5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Public chat preview</div>
              <div className="mt-0.5 text-sm">
                Share a link to demo chat live.
              </div>
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 mt-1 text-xs"
                onClick={copyWidgetPreviewUrl}
                disabled={!me.affiliate.sandboxOrganization}
              >
                <Copy className="mr-1 h-3 w-3" /> Copy preview URL
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOutboundOpen(true)}
              disabled={!me.affiliate.demoPhoneNumber || isPreview}
              title={
                isPreview
                  ? 'Preview mode — read only'
                  : !me.affiliate.demoPhoneNumber
                    ? 'Demo phone number required'
                    : 'Trigger a live demo outbound call'
              }
            >
              <PhoneOutgoing className="mr-2 h-4 w-4" /> Trigger demo outbound
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setResetOpen(true)}
              disabled={!me.affiliate.sandboxOrganization || isPreview}
              className="text-amber-700 hover:text-amber-800 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/30"
              title={isPreview ? 'Preview mode — read only' : undefined}
            >
              <RotateCw className="mr-2 h-4 w-4" /> Reset for next prospect
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Email teaser — the full inbox lives at /affiliate/email so it gets a
          top-level nav slot and the page doesn't bury a multi-pane tool inside
          a stack of stat cards. */}
      <Card className="card-inset">
        <CardContent className="py-4">
          <Link
            to={`/affiliate/email${linkQuery}`}
            className="flex items-center gap-4 -mx-2 px-2 py-1.5 rounded-md transition-colors hover:bg-muted/40"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-royal/20 to-brand-periwinkle/10 border border-brand-royal/20">
              <Mail className="h-4 w-4 text-brand-royal" strokeWidth={2.25} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold tracking-tight">Affiliate email</div>
              <div className="mt-0.5 text-xs text-muted-foreground leading-snug">
                Inbox, sent, compose, and signature for your partner alias.
              </div>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
          </Link>
        </CardContent>
      </Card>

      <Card className="card-inset">
        <CardHeader>
          <CardTitle className="text-base">Referred clients</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Signed up</TableHead>
                  <TableHead className="text-right">Earned</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referrals.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">No referred clients yet.</TableCell></TableRow>
                ) : referrals.map(row => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.organization_name}</TableCell>
                    <TableCell><Badge variant="outline">{row.status}</Badge></TableCell>
                    <TableCell>{row.subscription_tier} · {row.subscription_status}</TableCell>
                    <TableCell>{date(row.signup_at)}</TableCell>
                    <TableCell className="text-right">{money(Number(row.pending_cents) + Number(row.approved_cents) + Number(row.paid_cents))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card className="card-inset">
        <CardHeader>
          <CardTitle className="text-base">Commission ledger</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Invoice paid</TableHead>
                  <TableHead className="text-right">Commission</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {commissions.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">No commission entries yet.</TableCell></TableRow>
                ) : commissions.map(row => (
                  <TableRow key={row.id}>
                    <TableCell>{row.organization_name}</TableCell>
                    <TableCell><Badge variant="outline">{row.status}</Badge></TableCell>
                    <TableCell>{date(row.invoice_paid_at)}</TableCell>
                    <TableCell className="text-right">{money(row.commission_amount_cents)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset demo for next prospect?</DialogTitle>
            <DialogDescription>
              Wipes wizard-created agents, knowledge base, branding, and previous demo calls/chats. <strong>Keeps</strong> the demo phone number and the 4 sample leads (Maria, David, etc.) so the dashboard isn't empty during your next call.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)} disabled={resetting}>Cancel</Button>
            <Button variant="default" onClick={resetDemo} disabled={resetting}>
              {resetting ? 'Resetting…' : 'Reset demo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={outboundOpen} onOpenChange={setOutboundOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Trigger demo outbound call</DialogTitle>
            <DialogDescription>
              Molly will call this number using the agent currently configured on your sandbox. Use during demos to show "what happens when someone fills out a contact form." Limit: 5 calls/hour.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="phoneNumber">Phone number to call</Label>
              <Input
                id="phoneNumber"
                value={outboundForm.phoneNumber}
                onChange={e => setOutboundForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                placeholder="+1 (555) 555-1234"
                autoComplete="off"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="firstName">First name (optional)</Label>
                <Input
                  id="firstName"
                  value={outboundForm.firstName}
                  onChange={e => setOutboundForm(prev => ({ ...prev, firstName: e.target.value }))}
                  placeholder="John"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="caseType">Case type (optional)</Label>
                <Input
                  id="caseType"
                  value={outboundForm.caseType}
                  onChange={e => setOutboundForm(prev => ({ ...prev, caseType: e.target.value }))}
                  placeholder="DUI consultation"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Caller ID will show your demo phone number ({formatPhoneDisplay(me.affiliate.demoPhoneNumber)}).
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOutboundOpen(false)} disabled={outboundCalling}>Cancel</Button>
            <Button onClick={fireDemoOutbound} disabled={outboundCalling || !outboundForm.phoneNumber.trim()}>
              {outboundCalling ? 'Calling…' : 'Fire demo call'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
